import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CustomsCalculationsService, CustomsCalculation } from '../../../services/api/customs-calculations';
import { LanguageService } from '../../../services/i18n/language';
import { AuthService } from '../../../services/auth/auth';
import { ViewCalculationDetailsComponent } from '../../shared/customs-calculations/view-calculation-details/view-calculation-details';
import { Subscription } from 'rxjs';
import { PhoneClaimService } from '../../../services/phone-claim';
import { CALC_CABINET_ROLES, CalcSection, toCalcSection } from '../../shared/customs-calculations/calculation-sections';
import { CalcProgress, CalculationProgressService } from '../../../services/calculation-progress';

@Component({
  selector: 'app-view-calculation',
  standalone: true,
  imports: [CommonModule, RouterModule, ViewCalculationDetailsComponent],
  templateUrl: './view-calculation.html',
  styleUrl: './view-calculation.scss'
})
export class ViewCalculationComponent implements OnInit, OnDestroy {
  calculation: CustomsCalculation | null = null;
  loading = false;
  error: string | null = null;
  /** Раздел из адреса /calculations/:id/<раздел>; null — роль без кабинета (брокер, поставщик), страница целиком. */
  section: CalcSection | null = null;

  /** Расчёт ещё создаётся: ИИ разбирает товары — показываем ход работы вместо разделов. */
  progress: CalcProgress | null = null;
  private progressSub?: Subscription;

  private subscriptions = new Subscription();

  constructor(
    private customsCalculationsService: CustomsCalculationsService,
    private languageService: LanguageService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private calculationProgress: CalculationProgressService,
    private phoneClaim: PhoneClaimService,
    private cdr: ChangeDetectorRef
  ) {}

  /** Идёт создание (не завершено и без ошибки). */
  get processing(): boolean {
    return !!this.progress && !this.progress.done && !this.progress.error;
  }

  t(key: string, fallback: string): string {
    const value = this.languageService.translate(key);
    return value && value !== key ? value : fallback;
  }

  ngOnInit(): void {
    if (typeof window === 'undefined' || !window.localStorage) {
      console.warn('ViewCalculation: Running on server (SSR), skipping initialization');
      return;
    }

    setTimeout(() => {
      if (!this.authService.isAuthenticated()) {
        this.router.navigate(['/login']);
        return;
      }

      const inCabinet = CALC_CABINET_ROLES.includes(this.authService.getUser()?.role?.code ?? '');
      // Переход между разделами меняет только адрес — расчёт заново грузим лишь при смене id
      this.subscriptions.add(
        this.route.paramMap.subscribe(params => {
          const nextSection = inCabinet ? toCalcSection(params.get('section')) : null;
          // Другой раздел открывается в той же странице — роутер прокрутку не сбрасывает, делаем это сами
          if (this.section && nextSection !== this.section) {
            window.scrollTo({ top: 0 });
          }
          this.section = nextSection;
          const id = params.get('id');
          if (id && +id !== this.calculation?.id) {
            this.watchProgress(+id);
            this.loadCalculation(+id);
          }
          // Гость (ещё без телефона) видит только сводку: закрытый раздел по прямой ссылке → сводка и окно с телефоном
          if (id && this.section && this.section !== 'overview' && this.authService.isGuest()) {
            this.router.navigate(['/calculations', +id, 'overview'], { replaceUrl: true });
            this.phoneClaim.open(`/calculations/${id}/${this.section}`);
          }
          // Пока расчёт создаётся, остальные разделы пусты — держим клиента на «Стоимости поставки»
          if (id && this.processing && this.section && this.section !== 'overview') {
            this.router.navigate(['/calculations', +id, 'overview'], { replaceUrl: true });
          }
          this.cdr.markForCheck();
        })
      );
    }, 0);
  }

  ngOnDestroy(): void {
    this.progressSub?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  /** Расчёт только что создан со страницы «Новый расчёт» — следим за ходом; по готовности перечитываем его. */
  private watchProgress(id: number): void {
    this.progressSub?.unsubscribe();
    this.progress = null;
    if (!this.calculationProgress.isActive(id)) return;
    this.progressSub = this.calculationProgress.watch(id).subscribe(state => {
      const wasProcessing = this.processing;
      this.progress = state;
      if (wasProcessing && !this.processing) {
        this.loadCalculation(id);
      }
      this.cdr.markForCheck();
    });
  }

  loadCalculation(id: number): void {
    this.loading = true;
    this.error = null;

    this.subscriptions.add(
      this.customsCalculationsService.getCalculationById(id).subscribe({
        next: (calculation) => {
          this.calculation = calculation;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error loading calculation:', err);
          this.error = 'Ошибка при загрузке расчета';
          this.loading = false;
          this.cdr.markForCheck();
        }
      })
    );
  }

  onBack(): void {
    this.navigateToDashboard();
  }

  /** Админ смотрит чужой расчёт: видит то же, что клиент, но без его действий. */
  get isAdmin(): boolean {
    return this.authService.getUser()?.role?.code === 'ROLE_ADMIN';
  }

  /** «Заказать сопровождение» — сверху на всех разделах расчёта (кроме просмотра админом). */
  get showProcess(): boolean {
    return !this.isAdmin;
  }

  get processLabel(): string {
    if (this.section) {
      const [key, fallback] = this.isNewCalculation
        ? ['calcSections.startShipment', 'Заказать сопровождение']
        : ['calcSections.continueShipment', 'Продолжить оформление'];
      const label = this.languageService.translate(key);
      return label && label !== key ? label : fallback;
    }
    return this.calculation?.status === 'CREATED'
      ? (this.languageService.translate('public.viewCalculation.process') || 'Обработать')
      : (this.languageService.translate('public.viewCalculation.continueProcessing') || 'Продолжить обработку');
  }

  /** Заявка на сопровождение ещё не подтверждена (расчёт не в работе). */
  private get isNewCalculation(): boolean {
    return !this.calculation?.status || this.calculation.status === 'CREATED';
  }

  onProcessClick(): void {
    if (!this.calculation) return;
    // В кабинете новый расчёт сначала проходит заявку на сопровождение; подтверждённый — сразу на обработку
    const target = this.section && this.isNewCalculation ? 'support' : 'process';
    this.router.navigate(['/calculations', this.calculation.id, target]);
  }

  private navigateToDashboard(): void {
    const user = this.authService.getUser();
    const roleCode = user?.role?.code;

    switch (roleCode) {
      case 'ROLE_CARGO_OWNER':
        this.router.navigate(['/cargo-owner/dashboard/calculations']);
        break;
      case 'ROLE_FREIGHT_FORWARDER':
        this.router.navigate(['/freight-forwarder/dashboard']);
        break;
      case 'ROLE_CUSTOMS_BROKER':
        this.router.navigate(['/customs-broker/dashboard/calculations']);
        break;
      case 'ROLE_PUBLIC':
      default:
        this.router.navigate(['/public/dashboard']);
        break;
    }
  }
}
